import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast } from "primereact/toast";
import SettingTokenService from "src/server/SettingTokenService";
import { Typography } from "@mui/material";
import { prefetch, prefetchCustom } from "src/server/prefetchCache";
import getApiForOptionalPayload from "src/server/getApiForOptionalPayload";
import { setFavicon } from "src/utils/faviconUtils";
import postApiCall from "src/server/postApiCall";
import axios from "axios";
import { applyThemeCSSVariables } from "src/utils/themeUtils";

const _origin = window.location.origin.split("/");
const ORG_URL_DATA = {
  isHttps: _origin[0] === "http:" ? 0 : 1,
  domainName: _origin[2].replace(/^www\./, ""),
};

const getOrganizationDetails = () =>
  postApiCall("verifyOrganization", ORG_URL_DATA);

const getAutoLoginToken = (location) => {
  const hashRoute = location.hash.includes("/autologin?")
    ? location.hash.split("/autologin?")[1]
    : "";
  const query = location.search.startsWith("?")
    ? location.search.slice(1)
    : "";

  const tokenFromQuery = new URLSearchParams(query).get("token");
  if (tokenFromQuery) return tokenFromQuery;

  const rawToken = hashRoute || query;
  if (!rawToken || rawToken.includes("=")) return "";

  return decodeURIComponent(rawToken);
};

const applyOrgBranding = async (existingLogoText) => {
  try {
    const orgRes = await getOrganizationDetails();
    if (!orgRes) return;

    const favIcon = orgRes.organizationFavIcon;
    if (favIcon && favIcon !== "null") {
      localStorage.setItem("OrgFavIcon", favIcon);
      setFavicon(favIcon);
    }

    if (
      !existingLogoText &&
      orgRes.organizationLogoText &&
      orgRes.organizationLogoText !== "null"
    ) {
      localStorage.setItem("logoText", orgRes.organizationLogoText);
      localStorage.setItem("OrgLogoText", orgRes.organizationLogoText);
      document.title = orgRes.organizationLogoText;
    }
  } catch (err) {
    console.warn("Could not fetch org branding during auto-login:", err);
  }
};

const AutoLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef(null);
  const handledLocationRef = useRef("");
  const [isLoading, setIsLoading] = useState(true);
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    const locationKey = `${location.pathname}${location.search}${location.hash}`;
    if (handledLocationRef.current === locationKey) return;
    handledLocationRef.current = locationKey;

    if (!location.hash.includes("autologin")) {
      localStorage.removeItem("ApiToken");
    }

    const token = getAutoLoginToken(location);
    let callbackError = "";

    if (location.search) {
      const params = new URLSearchParams(location.search);
      callbackError = params.get("error") || params.get("message") || "";
    }

    if (token && location.pathname === "/auth/saml/callback") {
      window.history.replaceState({}, document.title, "/auth/saml/callback");
    }

    if (token) {
      localStorage.clear();
      verifyTokenFunc(token);
    } else if (callbackError) {
      toast.current.show({
        severity: "error",
        summary: "Microsoft login failed",
        detail: callbackError === "user_not_found"
          ? "Your Microsoft email is not registered for this organization."
          : callbackError,
        life: 3000,
      });
      setIsLoading(false);
      const redirectToSamlLogout = async () => {
        try {
          const orgRes = await getOrganizationDetails();
          if (orgRes?.redirectSAMLLogout) {
            sessionStorage.setItem("samlLogoutInProgress", "true");
            window.location.replace(orgRes.redirectSAMLLogout);
            return;
          }
        } catch (err) {
          console.warn("Could not fetch SAML logout URL:", err);
        }

        setTimeout(() => navigate("/login"), 3000);
      };
      redirectToSamlLogout();
    } else {
      setIsLoading(false);
      navigate("/login", { replace: true });
    }
  }, [location]);

  const verifyTokenFunc = async (token) => {
    try {
      localStorage.setItem("ApiToken", token);
      const res = await SettingTokenService("verifyUser", token);

      const updatedOrgDetails = {
        organizationId: res.orgDetails.orgId,
        organizationLogo: res.orgDetails.organizationLogo,
        organizationLogoText: res.orgDetails.orgLogoText,
        organizationName: res.orgDetails.orgName,
        lastUpdated: new Date().toISOString(),
      };

      setOrgName(res.orgDetails.orgName);
      localStorage.setItem("organizationDetails", JSON.stringify(updatedOrgDetails));
      localStorage.setItem("UID", res.userId);
      localStorage.setItem("UserName", res.userFullName);
      localStorage.setItem("RoleName", res.roleName);
      localStorage.setItem("ApiRoleName", res.roleName);
      localStorage.setItem("RoleType", res.roleType);
      localStorage.setItem("PrimaryOrg", res.orgDetails.isPrimary);
      localStorage.setItem("childOrg", JSON.stringify(res.orgDetails.childOrg));
      localStorage.setItem("OrgId", res.orgDetails.orgId);
      localStorage.setItem("orgCode", res.orgDetails.orgCode);
      localStorage.setItem("UserLogo", res.orgDetails.organizationLogo);
      localStorage.setItem("InnerLogo", res.orgDetails.innerLogo || "");
      localStorage.setItem("orgName", res.orgDetails.orgName);
      localStorage.setItem("ThemeColor", res.currentTheme.themeName);
      // Store raw API theme — colors are API-authoritative.
      localStorage.setItem("theme", JSON.stringify(res.currentTheme));
      // Apply CSS variables immediately so theme is active before navigation.
      applyThemeCSSVariables(res.currentTheme);
      sessionStorage.setItem("themeVerified", "true");
      localStorage.setItem("sessionTimeOut", res.orgDetails.sessionTimeOut);
      localStorage.setItem("showWelcomeMsg", res.showWelcomeMsg);

      if (res.roleType !== "role_super_admin") {
        localStorage.setItem("planId", res.orgDetails.planId);
        localStorage.setItem("hasDriverFile", res.orgDetails.hasDriverFile);
      } else {
        localStorage.removeItem("planId");
        localStorage.setItem("hasDriverFile", 1);
      }

      if (res.generalsettings && Array.isArray(res.generalsettings)) {
        const passwordChangeSetting = res.generalsettings.find(
          (setting) => setting.generalSettingId === 26
        );
        if (passwordChangeSetting) {
          localStorage.setItem(
            "passwordChangeSetting",
            JSON.stringify({
              generalSettingId: passwordChangeSetting.generalSettingId,
              generalSettingName: passwordChangeSetting.generalSettingName,
              isChecked: passwordChangeSetting.isChecked,
            })
          );
        }
      }

      const orgLogoText = res.orgDetails.orgLogoText;
      if (orgLogoText && orgLogoText !== "null") {
        localStorage.setItem("logoText", orgLogoText);
        localStorage.setItem("OrgLogoText", orgLogoText);
        document.title = orgLogoText;
      }

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Getting things ready for you...",
        life: 5000,
      });

      const API_URL = process.env.REACT_APP_API_URL;
      const authHeader = { headers: { Authorization: "Bearer " + token } };
      const orgId = res.orgDetails.orgId;
      const defaultFieldsUrl = orgId
        ? `/getDefaultCustomFields/1?OrgId=${orgId}`
        : `/getDefaultCustomFields/1`;

      const minDelay = new Promise((r) => setTimeout(r, 1000));

      await Promise.all([
        prefetch("getMenuSubmenuList"),
        prefetch("getHeaderAnnouncements"),
        prefetch("getThemeList"),
        prefetch("getTextNotifications"),
        prefetch("getReferenceHelpContent"),
        prefetch("getSystemSettings"),
        prefetchCustom("getDefaultCustomFields", getApiForOptionalPayload(defaultFieldsUrl)),
        prefetchCustom(
          "getProfileDetails",
          axios.get(`${API_URL}/getProfileDetails`, authHeader).then((r) => r.data.data)
        ),
        applyOrgBranding(orgLogoText),
        minDelay,
      ]);

      setIsLoading(false);
      if (res.roleType === "role_system_admin") {
        navigate("/admin/dashboard");
      } else if (res.roleType === "role_super_admin") {
        navigate("/su/dashboard");
      } else if (res.roleType === "role_user_students") {
        navigate("/user/dashboard");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (err) {
      setIsLoading(false);
      const errMsg =
        err?.response?.data?.errors ||
        err?.response?.data?.error ||
        "Authentication failed";
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: errMsg,
        life: 3000,
      });

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  };

  return (
    <div className="auto-login-container">
      <Toast ref={toast} />
      {isLoading && orgName && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            textAlign: "center",
          }}
        >
          <Typography variant="h2">Welcome to {orgName}!</Typography>
        </div>
      )}
    </div>
  );
};

export default AutoLogin;
